import { useCallback } from "react";

import { isEqual } from "lodash";
import {
  decodeJson,
  encodeJson,
  type QueryParamConfig,
  useQueryParam,
} from "use-query-params";

import { type DateInterval } from "@north-beam/nb-common";

type AnnotationParam = DateInterval | null;

const DateRangeJsonParam: QueryParamConfig<AnnotationParam> = {
  encode: (value) => encodeJson(value),
  decode: (value) => decodeJson(value),
  equals: (a, b) => isEqual(a, b),
};

export const useAnnotationParam = () => {
  const [annotationParam, setAnnotationParam] = useQueryParam<AnnotationParam>(
    "annotations",
    DateRangeJsonParam,
  );
  const [createAnnotationDateParam, setCreateAnnotationDateParam] =
    useQueryParam<string | undefined>("createAnnotationDate", undefined);
  const [
    filteredAnnotationDateRangeParam,
    setFilteredAnnotationDateRangeParam,
  ] = useQueryParam<AnnotationParam>(
    "filteredAnnotationDateRange",
    DateRangeJsonParam,
  );

  const listDateRangeAnnotations = useCallback(
    (value: AnnotationParam) => setAnnotationParam(value),
    [setAnnotationParam],
  );
  const listFilteredDateRangeAnnotations = useCallback(
    (value: AnnotationParam) => setFilteredAnnotationDateRangeParam(value),
    [setFilteredAnnotationDateRangeParam],
  );
  const removeFilteredDateRangeAnnotations = useCallback(
    () => setFilteredAnnotationDateRangeParam(null),
    [setFilteredAnnotationDateRangeParam],
  );
  const showCreateAnnotationOnDate = useCallback(
    (annotatedDate: string) => setCreateAnnotationDateParam(annotatedDate),
    [setCreateAnnotationDateParam],
  );
  const closeFlyout = useCallback(
    () => setAnnotationParam(null),
    [setAnnotationParam],
  );

  return {
    annotationParam,
    setAnnotationParam,
    isFlyoutOpen: Boolean(annotationParam),
    closeFlyout,
    listDateRangeAnnotations,

    filteredAnnotationDateRangeParam,
    removeFilteredDateRangeAnnotations,
    listFilteredDateRangeAnnotations,
    showCreateAnnotationOnDate,
    createAnnotationDateParam,
    setCreateAnnotationDateParam,
  };
};
